import { DashboardDataQuery } from '../DashboardData.generated';

import { filterByTimeframeAndDate, groupByDateAndSum } from './uniqueIssuesExtractors';

export interface IssuesCount {
	critical: number;
	serious: number;
	total: number;
	allIssues: number; // Total of all severities (not just critical + serious)
	day: Date;
}

export interface UniqueIssuesData {
	current: number;
	previous: number;
	critical: {
		current: number;
		previous: number;
	};
}

export interface DailyUniqueIssuesData {
	date: string;
	total: number;
	critical: number;
	serious: number;
}

export interface DashboardDataResult {
	regularPeriod: IssuesCount[];
	doublePeriod: IssuesCount[];
	uniqueIssues: UniqueIssuesData;
	totalIssues: UniqueIssuesData;
	dailyUniqueIssues: DailyUniqueIssuesData[];
	dailyAllIssues: DailyUniqueIssuesData[];
	lastUpdatedAt: number;
}

export const transformDashboardDataResults = (
	rawResults: DashboardDataQuery,
	selectedPeriodDays?: number
): DashboardDataResult => {
	// Regular period data - combine Web SDK and WFA data
	const regularPeriod = combineProductData(rawResults?.wsdkIssuesCount, rawResults?.wfaIssuesCount);

	// Double period data - combine Web SDK and WFA data
	const doublePeriod = combineProductData(
		rawResults?.wsdkIssuesCount2n,
		rawResults?.wfaIssuesCount2n
	);

	// Extract daily unique issues from score_web_products
	const dailyUniqueIssues = extractDailyUniqueIssues(
		rawResults?.scoreWebProductsDaily,
		selectedPeriodDays
	);
	// Extract daily all issues from score_web_products_daily_total
	const dailyAllIssues = extractDailyUniqueIssues(
		rawResults?.scoreWebProductsDailyTotal,
		selectedPeriodDays
	);

	// Extract unique issues from last day of scoreWebProductsDaily
	const uniqueIssues = extractLastDayIssues(dailyUniqueIssues);

	// Extract total issues from last day of scoreWebProductsDailyTotal
	const totalIssues = extractLastDayIssues(dailyAllIssues);

	const lastUpdatedAt = getMaxCreatedAt(rawResults?.scoreWebProductsDaily);

	return {
		regularPeriod,
		doublePeriod,
		uniqueIssues,
		totalIssues,
		dailyUniqueIssues,
		dailyAllIssues,
		lastUpdatedAt
	};
};

const getMaxCreatedAt = (records?: DashboardDataQuery['scoreWebProductsDaily']): number => {
	if (!records || records.length === 0) {
		return 0;
	}

	return records.reduce((max, record) => {
		if (!record?.created_at) {
			return max;
		}
		const timestamp = new Date(record.created_at).getTime();
		return timestamp > max ? timestamp : max;
	}, 0);
};

export const extractLastDayIssues = (dailyIssues: DailyUniqueIssuesData[]): UniqueIssuesData => {
	if (dailyIssues.length === 0) {
		return {
			current: 0,
			previous: 0,
			critical: { current: 0, previous: 0 }
		};
	}

	// dailyIssues is sorted by date, so last element is most recent (current)
	const currentDay = dailyIssues[dailyIssues.length - 1];
	const previousDay = dailyIssues.length > 1 ? dailyIssues[dailyIssues.length - 2] : null;

	return {
		current: currentDay.total,
		previous: previousDay?.total ?? 0,
		critical: {
			current: currentDay.critical,
			previous: previousDay?.critical ?? 0
		}
	};
};

/**
 * Extracts daily unique issues data from score_web_products
 * Filters by timeframe, groups by date, and sums values per date
 * @param scoreWebProducts - Raw score_web_products daily data from GraphQL
 * @param selectedPeriodDays - Selected period in days (for filtering by timeframe_days)
 * @returns Array of DailyUniqueIssuesData sorted by date
 */
const extractDailyUniqueIssues = (
	scoreWebProducts?:
		| DashboardDataQuery['scoreWebProductsDaily']
		| DashboardDataQuery['scoreWebProductsDailyTotal'],
	selectedPeriodDays?: number
): DailyUniqueIssuesData[] => {
	if (!scoreWebProducts || scoreWebProducts.length === 0) {
		return [];
	}

	const groupedByDate = filterByTimeframeAndDate(scoreWebProducts, selectedPeriodDays);
	return groupByDateAndSum(groupedByDate);
};

/**
 * Type guard to check if an issue has a valid run_time_truncDay
 */
const hasValidRunTime = (
	issue:
		| DashboardDataQuery['wsdkIssuesCount'][number]
		| DashboardDataQuery['wfaIssuesCount'][number]
): issue is NonNullable<typeof issue> & { run_time_truncDay: string } => {
	return issue !== null && typeof issue.run_time_truncDay === 'string';
};

/**
 * Processes issues data and adds to date map
 */
const processIssuesData = (
	issues: DashboardDataQuery['wsdkIssuesCount'] | DashboardDataQuery['wfaIssuesCount'],
	dateMap: Map<string, { critical: number; serious: number; total: number; allIssues: number }>
): void => {
	(issues || []).filter(hasValidRunTime).forEach((issue) => {
		const date = issue.run_time_truncDay;
		const existing = dateMap.get(date) || { critical: 0, serious: 0, total: 0, allIssues: 0 };
		dateMap.set(date, {
			...existing,
			critical: existing.critical + (issue.critical_issues_sum ?? 0),
			serious: existing.serious + (issue.serious_issues_sum ?? 0),
			allIssues: existing.allIssues + (issue.total_issues_sum ?? 0)
		});
	});
};

/**
 * Combines Web SDK and WFA product data by date
 * @param wsdkData - Web SDK issues count data
 * @param wfaData - WFA issues count data
 * @returns Combined IssuesCount array sorted by date
 */
const combineProductData = (
	wsdkData: DashboardDataQuery['wsdkIssuesCount'],
	wfaData: DashboardDataQuery['wfaIssuesCount']
): IssuesCount[] => {
	// Create a map to store all unique dates with their combined counts
	const dateMap = new Map<
		string,
		{ critical: number; serious: number; total: number; allIssues: number }
	>();

	// Process Web SDK data
	processIssuesData(wsdkData, dateMap);

	// Process WFA data
	processIssuesData(wfaData, dateMap);

	// Convert map to array, calculate total, and sort by date
	return Array.from(dateMap.entries())
		.map(([dateStr, counts]) => ({
			critical: counts.critical,
			serious: counts.serious,
			total: counts.critical + counts.serious, // Calculate total from critical + serious (for chart compatibility)
			allIssues: counts.allIssues, // Total of all severities
			day: new Date(dateStr)
		}))
		.sort((a, b) => a.day.getTime() - b.day.getTime());
};
